import Vue from 'vue';
import { store, mutations } from '@/store';
import { getDisplayStreamErrorMessage, streamChat } from '@/api/chat';
import { applySensitiveContentBlock } from '@/utils/sensitiveContent';
import { pollImageTask, resumeImageTasks, stopImageTask } from '@/utils/imageTaskService';

const GENERATED_IMAGE_MARKDOWN_REGEX = /!\[[^\]]*]\((https?:\/\/[^\s)]+|data:image\/[^\s)]+)\)/gi;

function extractGeneratedImageUrls(content = '') {
  if (typeof content !== 'string' || !content) {
    return [];
  }

  const urls = [];
  let match;
  while ((match = GENERATED_IMAGE_MARKDOWN_REGEX.exec(content)) !== null) {
    if (match[1]) {
      urls.push(match[1]);
    }
  }

  GENERATED_IMAGE_MARKDOWN_REGEX.lastIndex = 0;
  return [...new Set(urls)];
}

function syncAssistantGeneratedImages(message) {
  if (!message || message.role !== 'assistant') {
    return;
  }

  const generatedImageUrls = extractGeneratedImageUrls(message.content);
  if (generatedImageUrls.length === 0) {
    return;
  }

  message.generatedImageUrls = generatedImageUrls;
}

function setMessageField(message, key, value) {
  if (!message) return;
  if (Vue && Vue.set) Vue.set(message, key, value);
  else message[key] = value;
}

function appendTaskImages(message, result) {
  const urls = Array.isArray(result && result.images)
    ? result.images.map(item => typeof item === 'string' ? item : item && item.url).filter(Boolean)
    : [];
  if (!message || urls.length === 0) return;
  message.generatedImageUrls = [...new Set(urls)];
  const markdown = message.generatedImageUrls.map(url => `![Generated Image](${url})`).join('\n\n');
  const downloadLinks = message.generatedImageUrls.map(url => `[下载图片](${url})`).join('\n');
  if (!extractGeneratedImageUrls(message.content).length) {
    message.content = `${message.content || ''}${message.content ? '\n\n' : ''}${markdown}\n\n${downloadLinks}`;
  } else if (!/\[下载图片\]\(https?:\/\/[^)]+\)/i.test(message.content || '')) {
    message.content = `${message.content || ''}\n\n${downloadLinks}`;
  }
}

function startImageTaskForMessage(vm, message, taskPayload) {
  if (!message || !taskPayload || !taskPayload.task_id) return;
  const task = {
    taskId: String(taskPayload.task_id),
    status: taskPayload.status || 'queued',
    progress: taskPayload.progress == null ? 0 : Number(taskPayload.progress),
    generationEndpoint: taskPayload.generation_endpoint || '',
    taskEndpoint: taskPayload.task_endpoint || '',
    modelId: taskPayload.model_id || '',
    provider: taskPayload.provider || 'openai',
    model: taskPayload.model || '',
    createdAt: Date.now(),
    updatedAt: Date.now(),
    error: ''
  };

  setMessageField(message, 'imageTask', task);
  setMessageField(message, 'isGeneratingImage', true);
  mutations.finalizeLastMessage();

  pollImageTask(task, {
    onUpdate: (next) => {
      setMessageField(message, 'imageTask', next);
      mutations.finalizeLastMessage();
    },
    onComplete: (next, result) => {
      appendTaskImages(message, result);
      setMessageField(message, 'isGeneratingImage', false);
      setMessageField(message, 'imageTask', null);
      mutations.finalizeLastMessage();
      if (vm && vm.scrollToBottom) vm.scrollToBottom(true);
    },
    onError: (error, next) => {
      const failedTask = { ...next, status: error.code === 'IMAGE_TASK_TIMEOUT' ? 'timeout' : 'failed', error: error.message || '绘图任务失败', updatedAt: Date.now() };
      setMessageField(message, 'imageTask', failedTask);
      setMessageField(message, 'isGeneratingImage', false);
      mutations.finalizeLastMessage();
      if (vm && vm.scrollToBottom) vm.scrollToBottom(true);
    }
  }).catch(() => null);
}

function resumePendingImageTasks(vm) {
  return resumeImageTasks(store.history, {
    onUpdate: (message, next) => {
      setMessageField(message, 'imageTask', next);
      setMessageField(message, 'isGeneratingImage', true);
      mutations.finalizeLastMessage();
    },
    onComplete: (message, next, result) => {
      appendTaskImages(message, result);
      setMessageField(message, 'isGeneratingImage', false);
      setMessageField(message, 'imageTask', null);
      mutations.finalizeLastMessage();
      if (vm && vm.scrollToBottom) vm.scrollToBottom(true);
    },
    onError: (message, error, next) => {
      setMessageField(message, 'imageTask', { ...next, status: error.code === 'IMAGE_TASK_TIMEOUT' ? 'timeout' : 'failed', error: error.message || '绘图任务失败', updatedAt: Date.now() });
      setMessageField(message, 'isGeneratingImage', false);
      mutations.finalizeLastMessage();
    }
  });
}

function retryImageTaskForMessage(vm, message) {
  if (!message || !message.imageTask || !message.imageTask.taskId) return Promise.resolve();
  const task = { ...message.imageTask, status: 'queued', error: '', updatedAt: Date.now() };
  setMessageField(message, 'imageTask', task);
  setMessageField(message, 'isGeneratingImage', true);
  return pollImageTask(task, {
    onUpdate: (next) => {
      setMessageField(message, 'imageTask', next);
      mutations.finalizeLastMessage();
    },
    onComplete: (next, result) => {
      appendTaskImages(message, result);
      setMessageField(message, 'isGeneratingImage', false);
      setMessageField(message, 'imageTask', null);
      mutations.finalizeLastMessage();
      if (vm && vm.scrollToBottom) vm.scrollToBottom(true);
    },
    onError: (error, next) => {
      setMessageField(message, 'imageTask', { ...next, status: error.code === 'IMAGE_TASK_TIMEOUT' ? 'timeout' : 'failed', error: error.message || '绘图任务失败', updatedAt: Date.now() });
      setMessageField(message, 'isGeneratingImage', false);
      mutations.finalizeLastMessage();
    }
  }).catch(() => null);
}

function applySensitiveModelLock(aiMsgIndex) {
  const userMsg = store.history[aiMsgIndex - 1];
  const assistantMsg = store.history[aiMsgIndex];

  if (userMsg) {
    userMsg.sensitiveModelLock = true;
  }

  if (assistantMsg) {
    assistantMsg.sensitiveModelLock = true;
  }
}

// 会话级档位锁定：把后端首次判定的 tier 记到本轮 user+assistant 消息上，
// 后续请求经 optimizeContext 带回后端，从而跳过重复意图识别。
function applyTierLock(aiMsgIndex, tier) {
  if (tier === null || tier === undefined || tier === '') return;
  const userMsg = store.history[aiMsgIndex - 1];
  const assistantMsg = store.history[aiMsgIndex];

  if (userMsg) {
    userMsg.tierLock = tier;
  }

  if (assistantMsg) {
    assistantMsg.tierLock = tier;
  }
}

export class ChatService {
  static resumePendingImageTasks(vm) {
    return resumePendingImageTasks(vm);
  }

  static retryPendingImageTask(vm, message) {
    return retryImageTaskForMessage(vm, message);
  }

  static stopPendingImageTasks(messages = []) {
    (Array.isArray(messages) ? messages : []).forEach(message => {
      if (message && message.imageTask && message.imageTask.taskId) stopImageTask(message.imageTask.taskId);
    });
  }
  /**
   * 生成伙伴逻辑
   * @param {Object} vm - Vue 组件实例
   * @param {String} name - 角色名称
   */
  static async generateCharacterCard(vm, name) {
    mutations.addMessage({
      role: 'assistant',
      content: `正在为您创建伙伴：${name}...`,
      isThinking: true,
      timestamp: Date.now()
    });
    if (vm.scrollToBottom) vm.scrollToBottom(true);
    
    const aiMsgIndex = store.history.length - 1;

    try {
      const aisession = vm.sessionId || store.currentSessionId || '';
      const formData = new FormData();
      formData.append('name', name);
      if (aisession) {
        formData.append('aisession', aisession);
      }

      const response = await fetch('https://wm183.ktllq.cn/Aibrowser/generateCharacter', {
        method: 'POST',
        body: formData
      });

      const resData = await response.json();
      
      if (resData.code === 200 && resData.data) {
        const character = resData.data;
        
        // 添加或覆盖伙伴到 store
        mutations.addOrUpdateRole(character);

        // 自动选中该伙伴
        mutations.setActiveCompanions([character.id]);
        
        // 更新对话消息
        const currentMsg = store.history[aiMsgIndex];
        currentMsg.isThinking = false;
        currentMsg.content = `伙伴已创建，您现在可以和它一起工作了。`;
        if (vm.scrollToBottom) vm.scrollToBottom(true);
      } else {
        const currentMsg = store.history[aiMsgIndex];
        currentMsg.isThinking = false;
        currentMsg.content = `创建伙伴失败：${resData.msg || '未知错误'}`;
        if (vm.scrollToBottom) vm.scrollToBottom(true);
      }
    } catch (error) {
      console.error('Failed to generate character card:', error);
      const currentMsg = store.history[aiMsgIndex];
      currentMsg.isThinking = false;
      currentMsg.content = `创建伙伴失败，网络错误。`;
      if (vm.scrollToBottom) vm.scrollToBottom(true);
    }
  }

  /**
   * 统一的消息发送处理方法
   * @param {Object} vm - Vue 组件实例 (this)
   * @param {Object} options - 配置和回调钩子
   */
  static async sendMessage(vm, options = {}) {
    if (vm.isUploading) {
      mutations.addMessage({
        role: 'assistant',
        content: '文件上传中，请稍后再发送',
        timestamp: Date.now()
      });
      if (vm.scrollToBottom) vm.scrollToBottom(true);
      mutations.finalizeLastMessage();
      return;
    }

    // 允许在正式发送前执行自定义逻辑（如关闭弹窗、清除状态）
    if (options.onBeforeSend) {
      const shouldContinue = await options.onBeforeSend();
      if (shouldContinue === false) return;
    }

    const isManual = typeof options.payload === 'string';
    const textProp = options.textProp || 'inputText';
    const text = (isManual ? options.payload : vm[textProp] || '').trim();
    const currentAttachments = isManual ? [] : [...(vm.attachments || [])];

    if ((!text && currentAttachments.length === 0) || vm.isSending) return;

    vm.isSending = true;

    if (!isManual) {
      if (textProp in vm) vm[textProp] = '';
      vm.attachments = [];
    }

    // 重置输入框高度
    vm.$nextTick(() => {
      const inputBox = vm.$refs.chatInputBox;
      if (inputBox && inputBox.resetHeight) {
        inputBox.resetHeight();
      }
    });

    // 1. 添加用户消息
    mutations.addMessage({
      role: 'user',
      content: text,
      attachments: currentAttachments.map(att => ({
        ...att,
        url: att.url || ''
      })),
      imageUrl: currentAttachments.find(att => att.type === 'image')?.url || '',
      timestamp: Date.now(),
      isSearch: vm.webSearchEnabled || false,
      companionIds: Array.isArray(vm.activeCompanionIds) ? vm.activeCompanionIds.slice() : [],
      companionSnapshots: Array.isArray(vm.activeCompanions)
        ? vm.activeCompanions.map(companion => ({
          id: companion.id,
          name: companion.name,
          icon: companion.icon,
          systemPrompt: companion.systemPrompt
        }))
        : []
    });

    const userMsgIndex = store.history.length - 1;
    const aiMsgIndex = store.history.length;

    // 2. 允许在用户消息添加后、AI消息占位符添加前拦截（如命令模式、临时邮箱拦截）
    if (options.onUserMessageAdded) {
      const handled = await options.onUserMessageAdded(text, userMsgIndex);
      if (handled) {
        // 如果被拦截，重置发送状态并提前返回
        vm.isSending = false;
        return;
      }
    }

    // 3. (原先在这里处理的 handleAttachments 移到 AI 占位符渲染后，避免阻塞 UI)

    // 4. 添加 AI 消息占位符
    mutations.addMessage({
      role: 'assistant',
      content: '',
      thinking: '',
      isThinking: false,
      timestamp: Date.now(),
      isSearch: vm.webSearchEnabled || false,
      chatStatus: 'idle',
      statusText: '',
      workspaceSteps: [],
      companionIds: Array.isArray(vm.activeCompanionIds) ? vm.activeCompanionIds.slice() : [],
      companionSnapshots: Array.isArray(vm.activeCompanions)
        ? vm.activeCompanions.map(companion => ({
          id: companion.id,
          name: companion.name,
          icon: companion.icon,
          systemPrompt: companion.systemPrompt
        }))
        : []
    });

    if (vm.scrollToBottom) {
      vm.scrollToBottom(true);
    }

    vm.abortController = new AbortController();

    // 4.5. 允许在流式请求前执行异步准备工作（如抓取网页、查询知识库，此时 AI 占位符已渲染）
    if (options.handleAttachments) {
      await options.handleAttachments(text, currentAttachments, aiMsgIndex);
    }
    
    if (options.onBeforeStream) {
      await options.onBeforeStream(text, userMsgIndex, aiMsgIndex, currentAttachments);
    }

    // 5. 准备 Context
    let context = store.history.slice(0, -1);
    if (options.formatContext) {
      context = options.formatContext(context);
    }

    const systemContent = options.getSystemContent
      ? options.getSystemContent({ prompt: text, history: context })
      : '';
    if (systemContent) {
      context = [{ role: 'system', content: systemContent }, ...context];
    }

    const streamOptions = options.getStreamOptions
      ? options.getStreamOptions({ prompt: text, history: context })
      : {};
    if (!streamOptions.client_request_id) {
      streamOptions.client_request_id = `chat_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    }
    const pendingAssistantMsg = store.history[aiMsgIndex];
    if (pendingAssistantMsg) {
      if (vm.$set) {
        vm.$set(pendingAssistantMsg, 'clientRequestId', streamOptions.client_request_id);
      } else {
        pendingAssistantMsg.clientRequestId = streamOptions.client_request_id;
      }
    }

    // 6. 调用流式接口
    try {
      await streamChat(text, context, {
        onContent: (chunk) => {
          const currentMsg = store.history[aiMsgIndex];
          if (currentMsg.isThinking) currentMsg.isThinking = false;
          if (currentMsg.isGeneratingImage) currentMsg.isGeneratingImage = false;
          currentMsg.content += chunk;
          if (vm.scrollToBottom) vm.scrollToBottom();
        },
        onThinking: (chunk) => {
          const currentMsg = store.history[aiMsgIndex];
          currentMsg.thinking += chunk;
          currentMsg.isThinking = true;
          if (vm.scrollToBottom) vm.scrollToBottom();
        },
        onChatId: (id) => {
          if (options.onChatId) options.onChatId(id);
        },
        onMeta: (meta) => {
          if (meta && meta.type === 'sensitive_model_lock' && meta.locked) {
            applySensitiveModelLock(aiMsgIndex);
          }
          if (meta && meta.type === 'tier_lock' && meta.locked) {
            applyTierLock(aiMsgIndex, meta.tier);
          }
          if (options.onMeta) options.onMeta(meta, aiMsgIndex);
        },
        onImageStart: () => {
          const currentMsg = store.history[aiMsgIndex];
          vm.$set(currentMsg, 'isGeneratingImage', true);
          if (vm.scrollToBottom) vm.scrollToBottom();
        },
        onImageTask: (taskPayload) => {
          const currentMsg = store.history[aiMsgIndex];
          startImageTaskForMessage(vm, currentMsg, taskPayload);
        },
        onDecision: (decisionJson) => {
          if (options.onDecision) options.onDecision(decisionJson, aiMsgIndex);
        },
        onKnowledgeBaseJudging: (payload) => {
          const currentMsg = store.history[aiMsgIndex];
          if (!currentMsg) return;

          let status = payload || {};
          if (typeof status === 'string') {
            try {
              status = JSON.parse(status);
            } catch (e) {
              status = {};
            }
          }

          const setMsgField = (key, value) => {
            if (vm.$set) {
              vm.$set(currentMsg, key, value);
            } else {
              currentMsg[key] = value;
            }
          };

          if (status.status === 'start') {
            setMsgField('kbRelevanceJudging', true);
            setMsgField('kbJudgingContext', streamOptions.knowledge_base_context || []);
            setMsgField('chatStatus', 'kb_relevance');
            setMsgField('statusText', vm.$t
              ? vm.$t('browserAgentChat.kbJudgingStatus', { count: status.count || 0 })
              : `正在智能筛选 ${status.count || 0} 个知识库片段...`);
          } else if (status.status === 'done') {
            setMsgField('kbRelevanceJudging', false);
            const sourceKbContext = streamOptions.knowledge_base_context || [];
            const previousUserMsg = store.history[aiMsgIndex - 1];
            if (previousUserMsg && Array.isArray(sourceKbContext) && Array.isArray(status.indices)) {
              const selectedKbContext = status.indices
                .map(index => sourceKbContext[Number(index) - 1])
                .filter(Boolean);

              if (vm.$set) {
                vm.$set(previousUserMsg, 'kbContext', selectedKbContext.length > 0 ? selectedKbContext : null);
              } else {
                previousUserMsg.kbContext = selectedKbContext.length > 0 ? selectedKbContext : null;
              }

              if (vm.pageContext) {
                vm.pageContext.kbContext = selectedKbContext.length > 0 ? selectedKbContext : null;
              }
            }
            if (currentMsg.chatStatus === 'kb_relevance') {
              setMsgField('chatStatus', 'idle');
              setMsgField('statusText', '');
            }
          }
        },
        onSensitiveContent: () => {
          const currentMsg = pendingAssistantMsg || store.history[aiMsgIndex];
          applySensitiveContentBlock(currentMsg);
          vm.isSending = false;
          vm.abortController = null;
          if (vm.scrollToBottom) vm.scrollToBottom(true);
          mutations.finalizeLastMessage();
          if (options.onSensitiveContent) options.onSensitiveContent(aiMsgIndex);
        },
        onDone: () => {
          if (vm.kbOverrideRestarting || vm.workspaceToolPending) return;

          vm.isSending = false;
          vm.abortController = null;
          const currentMsg = store.history[aiMsgIndex];
          syncAssistantGeneratedImages(currentMsg);
          try {
            mutations.finalizeLastMessage();
          } catch (e) {
            console.error('Error in finalizeLastMessage:', e);
          }
          if (options.onDone) options.onDone(aiMsgIndex);
        },
        onError: (err) => {
          if ((vm.kbOverrideRestarting || vm.workspaceToolPending) && err.name === 'AbortError') return;

          if (err.name === 'AbortError') {
            console.log('Generation stopped by user');
          } else {
            console.error(err);
            const currentMsg = store.history[aiMsgIndex];
            const errorMessage = getDisplayStreamErrorMessage(err);
            if (errorMessage) {
              currentMsg.content += `${currentMsg.content ? '\n' : ''}${errorMessage}`;
            }
            if (vm.scrollToBottom) vm.scrollToBottom();
          }
          vm.isSending = false;
          vm.abortController = null;
          if (options.onError) options.onError(err);
        },
        signal: vm.abortController.signal
      }, currentAttachments, streamOptions);
    } catch (error) {
      if ((vm.kbOverrideRestarting || vm.workspaceToolPending) && error.name === 'AbortError') return;

      console.error('Failed to send message:', error);
      vm.isSending = false;
      vm.abortController = null;
    }
  }
}
